import { DropOverProps } from "../DropOver/DropOver";
import { DropOverMenu } from "../DropOverMenu/DropOverMenu";
import { RawHtml } from "../RawHtml/RawHtml";
import { TextSkeleton } from "../TextSkeleton/TextSkeleton";
import { TransparentButtonList } from "../TransparentButtonList/TransparentButtonList";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import styles from "./PreviewMenu.module.less";
import { NoResults } from "../NoResults/NoResults";
import { never } from "client_legacy/ndaw/errors";
import { NumberFormatter, useNumberFormatter } from "client/Hooks";
import { registerSizeListener, SizeInformation } from "client_legacy/ndaw/sizing";
import { ensureNotNil } from "client/Misc/utils";

export type PreviewMenuContentDefinition =
	| {
		state: "loading";
	}
	| {
		state: "success";
		onApply: () => void;
		onRetry: () => void;
		previewHtml: string;
	}
	| {
		state: "error";
		onRetry: () => void;
	}
	| {
		state: "content-too-large";
	};

export type PreviewMenuProps = {
	target: HTMLElement;
	trackingId: string;
	onClose: () => void;
	targetAlignment?: DropOverProps["targetAlignment"];
	contentAlignment?: DropOverProps["contentAlignment"];
	title: string;
	content: PreviewMenuContentDefinition;
	maxContentLength: number;
};

export const PreviewMenu = (props: PreviewMenuProps): ReactElement => {
	const {
		target,
		content,
		trackingId,
		onClose,
		targetAlignment,
		contentAlignment,
		title,
		maxContentLength
	} = props;

	const [lastContentSize, setLastContentSize] = useState<SizeInformation | null>(null);
	const resultContentContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			if (content.state !== "success") {
				return;
			}

			const container: HTMLDivElement = ensureNotNil(resultContentContainerRef.current);

			return registerSizeListener(container, setLastContentSize);
		},
		[content.state]
	);

	const numberFormatter: NumberFormatter = useNumberFormatter({
		format: "thousand-separator",
		culture: currentCulture
	});

	const renderContent = (): ReactElement => {
		switch (content.state) {
			case "loading":
				return (
					<div
						className={styles.content}
						style={{
							width: lastContentSize?.width,
							height: lastContentSize?.height
						}}
					>
						<h4 className={styles.title}>{title}</h4>
						<div className={styles.preview}>
							<p className={styles.skeletonParagraph}><TextSkeleton /></p>
							<p className={styles.skeletonParagraph}><TextSkeleton /></p>
							<p className={styles.skeletonParagraph}><TextSkeleton /></p>
						</div>
					</div>
				);
			case "error":
				return (
					<div className={styles.content}>
						<h4 className={styles.title}>{title}</h4>
						<div className={styles.errorContainer}>
							<NoResults
								title={localize("_NotAbleToGenerateASuggestion")}
								message={localize("_TheSuggestionCouldNotBeGeneratedPleaseTryAgain")}
								icon="gear"
								onRetry={content.onRetry}
							/>
						</div>
					</div>
				);
			case "content-too-large":
				return (
					<div className={styles.content}>
						<h4 className={styles.title}>{title}</h4>
						<div className={styles.errorContainer}>
							<NoResults
								title={localize("_TextIsTooLongToImprove")}
								message={{
									template: localize("_ImproveTextMaxCharactersLimitMessage"),
									replacements: [
										{
											what: "{{N}}",
											filler: numberFormatter(maxContentLength)
										}
									]
								}}
								icon="gear"
								onRetry={onClose}
								retryText={localize("_Close")}
							/>
						</div>
					</div>
				);
			case "success":
				break;
			default:
				never(content);
		}

		return (
			<div
				ref={resultContentContainerRef}
				className={styles.content}
			>
				<h4 className={styles.title}>{title}</h4>
				<div className={styles.preview}>
					<RawHtml
						html={content.previewHtml}
					/>
				</div>
				<hr className={styles.footerDivider} />
				<TransparentButtonList>
					<TransparentButtonList.Item
						icon={{
							kind: "font",
							name: "check",
							color: "success"
						}}
						text={localize("_Insert")}
						trackingId="insert"
						onClick={() => content.onApply()}
					/>
					<TransparentButtonList.Item
						icon={{
							kind: "font",
							name: "xmark",
							color: "danger"
						}}
						text={localize("_Discard")}
						trackingId="discard"
						onClick={onClose}
					/>
					<TransparentButtonList.Item
						icon="arrow-rotate-right"
						text={localize("_Retry")}
						trackingId="retry"
						onClick={content.onRetry}
					/>
				</TransparentButtonList>
			</div>
		);
	};

	return (
		<DropOverMenu
			target={target}
			trackingId={trackingId}
			title={title}
			onClose={onClose}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			hasResponsiveDropOver={false}
			shouldStayOpenOnScroll
		>
			{renderContent()}
		</DropOverMenu>
	);
};
PreviewMenu.displayName = "PreviewMenu";

const currentCulture: string = getCurrentCulture();